// core/services/messagesService.ts — B4b: API-backed conversations/messages.
//
// Thin fetch wrappers over DRF /api/conversations/. conversations() hydrates
// the store (per-viewer payload incl. embedded messages); messages()/totalUnread()
// stay store-reading. Signatures + _resetForTest unchanged.

import type {
  Conversation,
  ConversationInviteSummary,
  Message,
  ConversationType,
} from '@/core/models/message';
import { useMessageStore } from '@/core/stores/messageStore';
import { seedConversations } from '@/core/data/messages';
import { api } from '@/core/lib/apiClient';

function getConversations(): Conversation[] {
  return useMessageStore.getState().conversations;
}

// Poll-vs-send race guard. refresh() replaces the whole store from a server
// snapshot; if a send is mid-flight (or completed while the snapshot was in
// transit) that snapshot predates the sent message, and applying it would make
// the message vanish from the open thread until the next poll. Track sends and
// have refresh() drop any snapshot a send overlapped — the next poll includes
// the message anyway, because the server already has it.
let pendingSends = 0;
let sendEpoch = 0;

/** Result of respondInvite: accept hands back the now-joined conversation;
 *  decline hands back nothing (the server deliberately doesn't serve the full
 *  conversation to someone who just turned it down). */
export type RespondInviteResult =
  | { status: 'accepted'; conversation: Conversation }
  | { status: 'declined' };

/**
 * An already-uploaded DM image, as returned by mediaService.upload('dm', file).
 * The key is what the server stores; width/height are the dimensions it decoded
 * (passed back so the bubble can reserve the exact box).
 */
export interface OutgoingMessageImage {
  key: string;
  width?: number;
  height?: number;
}

export const messagesService = {
  async conversations(filter: 'all' | ConversationType): Promise<Conversation[]> {
    const all = await api<Conversation[]>('/conversations/');
    useMessageStore.getState().setConversations(all);
    if (filter === 'all') return all;
    return all.filter((c) => c.type === filter);
  },

  async messages(convoId: string): Promise<Message[]> {
    const c = getConversations().find((x) => x.id === convoId);
    return c ? c.messages : [];
  },

  /**
   * Send a message. `image` is an ALREADY-UPLOADED attachment (the caller does
   * mediaService.upload('dm', file) first) — the wire only ever carries the
   * key + dimensions, never the file. `body` may be empty when an image is
   * present; empty body AND no image is a 400 `validation` server-side.
   *
   * The image keys are spread in only when there IS an image, so a plain text
   * send keeps the exact `{ body }` payload it has always had.
   */
  async send(convoId: string, body: string, image?: OutgoingMessageImage): Promise<void> {
    pendingSends += 1;
    try {
      const message = await api<Message>(`/conversations/${convoId}/send/`, {
        method: 'POST',
        body: {
          body,
          ...(image
            ? {
                imageKey: image.key,
                ...(image.width ? { imageWidth: image.width } : {}),
                ...(image.height ? { imageHeight: image.height } : {}),
              }
            : {}),
        },
      });
      useMessageStore.getState().appendMessage(convoId, message);
    } finally {
      pendingSends -= 1;
      sendEpoch += 1;
    }
  },

  /**
   * Re-fetch the whole conversation list and replace the store — the polling
   * path that keeps an open Messages page live without a manual page refresh.
   * The snapshot is dropped (not applied) when a send overlapped it; see the
   * race-guard note above.
   */
  async refresh(): Promise<void> {
    const epochAtStart = sendEpoch;
    const all = await api<Conversation[]>('/conversations/');
    if (pendingSends > 0 || sendEpoch !== epochAtStart) return;
    useMessageStore.getState().setConversations(all);
  },

  async totalUnread(): Promise<number> {
    return getConversations().reduce((sum, c) => sum + c.unread, 0);
  },

  async markRead(convoId: string): Promise<void> {
    await api<Conversation>(`/conversations/${convoId}/read/`, { method: 'POST' });
    useMessageStore.getState().markConversationRead(convoId);
  },

  /**
   * Start (or resolve) a DM with a single handle. Idempotent — an existing
   * pair thread comes back 200; a new one 201. Either way the store gains it.
   * Throws ApiError: dm_not_allowed (403) | validation (400) | not_found (404).
   */
  async createDm(handle: string): Promise<Conversation> {
    const convo = await api<Conversation>('/conversations/', {
      method: 'POST',
      body: { type: 'dm', handles: [handle] },
    });
    useMessageStore.getState().upsertConversation(convo);
    return convo;
  },

  /**
   * Create a titled group with the given member handles (creator is added
   * automatically, server-side). Throws ApiError: validation (400, fields.title
   * or fields.handles) | not_found (404, fields.handle) | group_cap (403, fields.cap).
   */
  async createGroup(title: string, handles: string[]): Promise<Conversation> {
    const convo = await api<Conversation>('/conversations/', {
      method: 'POST',
      body: { type: 'group', title, handles },
    });
    useMessageStore.getState().upsertConversation(convo);
    return convo;
  },

  /**
   * Invite one more handle into an existing group. Throws ApiError:
   * not_participant (403) | validation (400) | not_found (404) |
   * already_member (409) | already_invited (409) | group_cap (403).
   */
  async invite(conversationId: string, handle: string): Promise<Conversation> {
    const convo = await api<Conversation>(`/conversations/${conversationId}/invite/`, {
      method: 'POST',
      body: { handle },
    });
    useMessageStore.getState().upsertConversation(convo);
    return convo;
  },

  /**
   * Accept a message request (1.24.00). The thread moves out of Requests into
   * the inbox; the server hands back the full conversation with `pending`
   * flipped, so the store is upserted from the response rather than patched
   * locally. Throws ApiError: not_participant (403) | already_accepted (409).
   */
  async accept(convoId: string): Promise<Conversation> {
    const convo = await api<Conversation>(`/conversations/${convoId}/accept/`, { method: 'POST' });
    useMessageStore.getState().upsertConversation(convo);
    return convo;
  },

  /**
   * Decline a message request (1.24.00). This DELETES the conversation for both
   * participants server-side — the wire answers only `{ok:true}`, never a
   * conversation — so the local row is removed rather than merged.
   * Throws ApiError: not_participant (403).
   */
  async decline(convoId: string): Promise<void> {
    await api<{ ok: true }>(`/conversations/${convoId}/decline/`, { method: 'POST' });
    useMessageStore.getState().removeConversation(convoId);
  },

  /** The caller's own pending group invites, newest first. */
  async listInvites(): Promise<ConversationInviteSummary[]> {
    return api<ConversationInviteSummary[]>('/conversations/invites/');
  },

  /**
   * Accept or decline a pending invite. Accepting hydrates the store with the
   * now-joined conversation and returns it. Declining returns only
   * {"ok":true,"status":"declined"} on the wire — NOT a conversation — so the
   * store is deliberately left untouched on decline.
   */
  async respondInvite(id: string | number, accept: boolean): Promise<RespondInviteResult> {
    const res = await api<Conversation | { ok: true; status: 'declined' }>(
      `/conversations/invites/${id}/respond/`,
      { method: 'POST', body: { accept } },
    );
    if ('ok' in res && res.ok) {
      return { status: 'declined' };
    }
    const convo = res as Conversation;
    useMessageStore.getState().upsertConversation(convo);
    return { status: 'accepted', conversation: convo };
  },

  _resetForTest(): void {
    useMessageStore.getState().setConversations(
      seedConversations.map((c) => ({
        ...c,
        messages: c.messages.map((m) => ({ ...m })),
      })),
    );
  },
} as const;
